import { compact } from 'lodash';

import type { Fund, ItemId, LibrarySearchResult, PrivateAssetSearchModeEnum } from 'venn-api';
import { searchHelpArticles } from 'venn-api';
import type { LibraryItemType } from 'venn-utils';
import { analyticsService, getCreatedBy, logExceptionIntoSentry } from 'venn-utils';

import type { Placeholders, SearchFilter, SearchMenuItem, SearchMenuItemCategory, SearchType } from './types';
import {
  fundToSearchMenuItem,
  getEntityFilters,
  getInvestmentSources,
  getPrivateAssetSearchMode,
  mapItemIds,
  mapSearchResultToAnalysisSubject,
  sortDefaultResults,
  tagsInSearchCheck,
} from './utils';

export type HelpCenterId = 'intercom' | 'salesforce';

// TODO: add some caching using react query
export const searchHelp = async (
  query: string,
  onResults: (items: SearchMenuItem[], totalResults: number) => void,
  helpCenterId: HelpCenterId,
) => {
  const articleResults = await searchHelpArticles({ name: query, from: 0, size: 10, helpCenterId });
  const totalResults = articleResults.content.total;
  onResults(
    articleResults.content.results.map((item) => ({ category: 'article', label: item.title, helpArticle: item })),
    totalResults,
  );
};

export const buildSearchParams = ({
  query,
  selectedFilters,
  privateAssetSearchModeDefault,
  maxResults,
  excludedItems,
  investmentsOnly,
  portfoliosOnly,
  defaultSearch,
  proxyable,
  placeholders,
  includeTagsSearch,
  itemTypes,
}: {
  query: string;
  selectedFilters: SearchFilter[];
  privateAssetSearchModeDefault: PrivateAssetSearchModeEnum;
  maxResults: number;
  excludedItems?: ItemId[];
  investmentsOnly?: boolean;
  portfoliosOnly?: boolean;
  defaultSearch?: boolean;
  proxyable?: boolean;
  placeholders?: Placeholders;
  includeTagsSearch?: boolean;
  itemTypes?: LibraryItemType[];
}) => {
  const { fundIds: excludedFundIds, portfolioIds: excludedPortfolioIds } = mapItemIds(excludedItems);
  const { fundIds: placeholderFundIds, portfolioIds: placeholderPortfolioIds } = mapItemIds(placeholders?.ids);
  const selectedAssetTypes = compact(selectedFilters.map((filter) => filter.assetType));
  const selectedItemTypes = compact([...selectedFilters.map((filter) => filter.itemType), ...(itemTypes ?? [])]);
  const selectedQuickFilters = compact(selectedFilters.map((filter) => filter.quickFilter));
  const selectedPrivateAssetSearchModes = compact(selectedFilters.map((filter) => filter.privateAssetSearchMode));
  const privateAssetSearchMode = getPrivateAssetSearchMode(
    selectedPrivateAssetSearchModes,
    privateAssetSearchModeDefault,
  );

  const recentlyViewed = defaultSearch && !placeholders;

  return {
    name: query,
    assetTypes: selectedAssetTypes?.length ? selectedAssetTypes : undefined,
    recentlyViewed,
    entityFilters: getEntityFilters(selectedItemTypes, investmentsOnly, portfoliosOnly),
    excludedFundIds,
    excludedPortfolioIds,
    privateAssetSearchMode,
    proxyable,
    pageSize: maxResults,
    createdBy: getCreatedBy(selectedQuickFilters),
    investmentSources: getInvestmentSources(selectedItemTypes),
    fundIds: defaultSearch ? placeholderFundIds : undefined,
    portfolioIds: defaultSearch ? placeholderPortfolioIds : undefined,
    includeTagsSearch: tagsInSearchCheck(
      Boolean(includeTagsSearch),
      Boolean(defaultSearch),
      selectedItemTypes,
      selectedAssetTypes,
      placeholders,
      privateAssetSearchMode,
    ),
  };
};

// TODO: add some caching using react query
export const search = async (
  query: string,
  /**
   * On every new search this method is queried with the formatted search result data.
   * The parent component can use it to store the data in its state.
   */
  onResults: (items: SearchMenuItem[], totalResults: number) => void,
  searchApi: SearchType,
  getDefaultInvestments: (proxySetId: string | undefined) => Promise<Fund[]>,
  setLoading: React.Dispatch<React.SetStateAction<boolean>>,
  location: string,
  selectedFilters: SearchFilter[],
  setOpenedTags: (items: SearchMenuItem[] | []) => void,
  privateAssetSearchModeDefault: PrivateAssetSearchModeEnum,
  maxResults: number,
  proxySetId: string | undefined,
  excludedItems?: ItemId[],
  investmentsOnly?: boolean,
  portfoliosOnly?: boolean,
  defaultSearch?: boolean,
  showDefaultSearch?: boolean,
  proxyable?: boolean,
  placeholders?: Placeholders,
  includeTagsSearch?: boolean,
  itemTypes?: LibraryItemType[],
) => {
  // If we are not showing default view results, don't make a request
  if (defaultSearch && !showDefaultSearch) {
    return;
  }
  try {
    setLoading(true);

    const searchParams = buildSearchParams({
      query,
      selectedFilters,
      privateAssetSearchModeDefault,
      maxResults,
      excludedItems,
      investmentsOnly,
      portfoliosOnly,
      defaultSearch,
      proxyable,
      placeholders,
      includeTagsSearch,
      itemTypes,
    });

    const response = await searchApi(searchParams);
    const results: LibrarySearchResult = response.content;

    if (!defaultSearch) {
      const selectedQuickFilters = compact(selectedFilters.map((filter) => filter.quickFilter));
      const selectedItemTypes = compact(selectedFilters.map((filter) => filter.itemType));

      analyticsService.searchQueryEntered({
        query: query ?? '',
        totalResults: results.total,
        visibleResults: results.results.length,
        location,
        quickFilters: selectedQuickFilters,
        typeFilters: selectedItemTypes,
      });
    }

    if (defaultSearch && !results?.results.length && !portfoliosOnly) {
      const sampleInvestments = await getDefaultInvestments(proxySetId);
      const sampleItems = sampleInvestments.map((investment) => fundToSearchMenuItem(investment, proxySetId));
      onResults(sampleItems, sampleItems.length);
    } else {
      let type: SearchMenuItemCategory | undefined;
      if (defaultSearch) {
        sortDefaultResults(results, placeholders?.ids);
        type = placeholders?.type ?? 'recent';
      }
      onResults(mapSearchResultToAnalysisSubject({ result: results, type, proxySetId }), results.total);
      setOpenedTags([]);
    }
    setLoading(false);
  } catch (e) {
    // @ts-expect-error fixme: ts errors from upgrade to typescript 5.5.3
    if (e.name === 'AbortError') {
      return;
    }
    // @ts-expect-error fixme: ts errors from upgrade to typescript 5.5.3
    logExceptionIntoSentry(e);
  }
};
